import { ChangeEvent, Dispatch, SetStateAction, useCallback } from 'react';

import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { MspCategoryDigitCodesEnum } from 'APIPackage/KnmModule/enums/MspCategoryDigitCodesEnum';
import { mspCodesByMspCategoryDigitCodes } from 'KnmPackage/MainModule/constants/mspCodesByMspCategoryDigitCodes';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { useKnmSetLegalEntityDataAction } from 'KnmPackage/MainModule/hooks/useKnmSetLegalEntityDataAction';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { IKnmDistrictSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { ICommonFormRef } from 'KnmErknmPackage/MainModule/interfaces/ICommonFormRef';
import { findRegionSelectOption } from 'KnmPackage/MainModule/components/regions';

import { IDecisionErknmSubjectFieldPaths } from './useDecisionErknmSubjectFields';

// eslint-disable-next-line @typescript-eslint/naming-convention
interface UseDecisionErknmSubjectHandlersProps {
  formRef: ICommonFormRef | undefined;
  subjectFieldNames: IDecisionErknmSubjectFieldPaths;
  isUR: boolean;
  isIP: boolean;
  setCheckByInnMessage?: Dispatch<SetStateAction<string | undefined>>;
  rafpNza: string | undefined;
  setRafpNzaNotFoundText: Dispatch<SetStateAction<string | undefined>>;
  isRafpNzaNotFound: boolean;
  rafpNotFoundMessage: string;
  districtSelectOptions: IKnmDistrictSelectOption[];
}

export const useDecisionErknmSubjectHandlers = ({
  formRef,
  subjectFieldNames,
  isUR,
  isIP,
  setCheckByInnMessage,
  rafpNza,
  setRafpNzaNotFoundText,
  isRafpNzaNotFound,
  rafpNotFoundMessage,
  districtSelectOptions,
}: UseDecisionErknmSubjectHandlersProps) => {
  const changeFormField = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (field: string, value1: any) => formRef?.current.change(field as keyof IKnmForm, value1 as IKnmForm),
    [formRef],
  );

  const resetFields = useCallback(() => {
    const change = (field: string) => changeFormField(field, undefined);

    change(subjectFieldNames.isFiz);
    change(subjectFieldNames.snils);
    change(subjectFieldNames.originalName);
    change(subjectFieldNames.inn);
    change(subjectFieldNames.ogrn);
    change(subjectFieldNames.locationAddress);
    change(subjectFieldNames.country);
    change(subjectFieldNames.kpp);
    change(subjectFieldNames.rafpNza);
    change(subjectFieldNames.document);
    change(subjectFieldNames.nkoForeignAgentsRegistryNumber);
    change(subjectFieldNames.nkoForeignAgentsRegistryInclusionDate);
    change(subjectFieldNames.nkoForeignAgentsRegistryExclusionDate);
    change(subjectFieldNames.isNko);
    change(subjectFieldNames.mspCode);
    change(subjectFieldNames.mspRegistryDate);
    change(subjectFieldNames.isSonko);
    change(subjectFieldNames.organizationGuid);
    change(subjectFieldNames.firstOrganizationName);
    change(subjectFieldNames.dateCreate);
    change(subjectFieldNames.checkSubjectResult);
    change(subjectFieldNames.smevItAccreditationRequestStatus);
    change(subjectFieldNames.isPresentInItRegistry);
    change(subjectFieldNames.itAccreditationResponse);
    change(subjectFieldNames.guid);
    change(subjectFieldNames.smevRequestStatus);
    change(subjectFieldNames.workAddress);
  }, [formRef]);

  const handleLegalEntityAutoCompleteSelect = useCallback(
    async (legalEntityLookup: ILegalEntityLookup) => {
      const change = changeFormField;

      const {
        ogrn: legalEntityOgrn,
        inn: legalEntityInn,
        orgName: legalEntityOrgName,
        leAddress: legalEntityLeAddress,
        mspCategory: legalMspCategory,
        workAddress: legalEntityWorkAddress,
        mspRegistryDate: legalMspRegistryDate,
        leAddressRegionCode,
        leAddressIsFromFias,
      } = legalEntityLookup;

      change(subjectFieldNames.isAutocomplete, true);
      if (legalEntityOgrn) {
        change(subjectFieldNames.ogrn, legalEntityOgrn);
      }
      if (legalEntityInn) {
        change(subjectFieldNames.inn, legalEntityInn);
      }
      if (legalEntityOrgName) {
        change(subjectFieldNames.originalName, legalEntityOrgName);
      }
      if (legalEntityLeAddress && isUR) {
        change(subjectFieldNames.locationAddress, legalEntityLeAddress);
        const isInFias = leAddressIsFromFias === 'true';

        change(subjectFieldNames.locationnotInFIAS, !isInFias);
        change(subjectFieldNames.locationRegionCodeFromFns, leAddressRegionCode);

        const regionOption = await findRegionSelectOption(
          legalEntityLeAddress,
          leAddressRegionCode || undefined,
          districtSelectOptions,
        );

        const finalLocationRegionField = isInFias
          ? subjectFieldNames.locationfiasRegion
          : subjectFieldNames.locationregion;

        change(finalLocationRegionField, regionOption);
      }
      if (legalEntityLeAddress && isIP) {
        change(subjectFieldNames.factualAddress, legalEntityLeAddress);
      }
      if (legalEntityWorkAddress && isUR) {
        change(subjectFieldNames.workAddress, legalEntityWorkAddress);
      }
      if (legalMspCategory) {
        change(
          subjectFieldNames.mspCode,
          mspCodesByMspCategoryDigitCodes[Number(legalMspCategory) as MspCategoryDigitCodesEnum],
        );
      }
      if (legalMspRegistryDate) {
        change(subjectFieldNames.mspRegistryDate, legalMspRegistryDate);
      }
    },
    [formRef, isUR, isIP],
  );

  const { dispatchKnmSetLegalEntityData } = useKnmSetLegalEntityDataAction();

  const handleAutoCompleteClear = useCallback(() => {
    const change = (field: string) => changeFormField(field, undefined);

    change(subjectFieldNames.ogrn);
    change(subjectFieldNames.inn);
    change(subjectFieldNames.originalName);
    change(subjectFieldNames.locationAddress);
    change(subjectFieldNames.factualAddress);
    change(subjectFieldNames.workAddress);
    change(subjectFieldNames.locationregion);
    change(subjectFieldNames.locationfiasRegion);
    dispatchKnmSetLegalEntityData(undefined);
    formRef?.current.change(subjectFieldNames.isAutocomplete, false);
    const objects = formRef?.current.getFieldState('objects' as keyof IKnmForm)?.value as IDecisionForm['objects'];
    const autocompleteObjectIndex = objects?.findIndex(object => object.autocomplete);

    if (typeof autocompleteObjectIndex === 'number' && autocompleteObjectIndex >= 0) {
      const autocompleteFieldName = `objects[${autocompleteObjectIndex}].autocomplete` as keyof IKnmForm;

      formRef?.current.change(autocompleteFieldName, false);
    }
  }, [dispatchKnmSetLegalEntityData, formRef]);

  const onClear = useCallback(() => {
    formRef?.current.change(subjectFieldNames.organizationType);
    resetFields();
    handleAutoCompleteClear();
  }, []);

  const subjectTypeChange = useCallback(
    (value: IDictionarySelectOption<ITargetEntity> | undefined) => {
      resetFields();
      handleAutoCompleteClear();
      formRef?.current.change(subjectFieldNames.isFiz, value?.label === KnmSubjectTypesEnum.FIZ);
      setCheckByInnMessage && setCheckByInnMessage(undefined);
    },
    [resetFields, formRef],
  );

  const handleRafpNzaChange = useCallback(
    (v: ChangeEvent<HTMLInputElement>) => {
      // Сбросить ИНН и КПП
      if (v.target.value) {
        formRef?.current.change(subjectFieldNames.inn as keyof IKnmForm, undefined);
        formRef?.current.change(subjectFieldNames.kpp as keyof IKnmForm, undefined);
        if (v.target.value !== rafpNza) {
          setRafpNzaNotFoundText(undefined);
        } else if (isRafpNzaNotFound) {
          setRafpNzaNotFoundText(rafpNotFoundMessage);
        }
      }
    },
    [formRef, subjectFieldNames],
  );

  return {
    resetFields,
    handleLegalEntityAutoCompleteSelect,
    handleAutoCompleteClear,
    onClear,
    subjectTypeChange,
    handleRafpNzaChange,
  };
};
